// Copyright 2019 Drivetrain Hub LLC
// Vuex module for managing state of user session.
// `namespaced` option applies to getters, actions, and mutations; state is always namespaced.
//  To persist user and organization data after a page refresh, write it to localStorage instead.
//  Ex) https://jasonwatmore.com/post/2018/07/06/vue-vuex-jwt-authentication-tutorial-example

import {getObjectById} from "../helpers";
import {apolloQuery} from "../../config/apollo";
import {datetimeNow} from "../../assets/js/utils";
import requests from "../../requests";
import gql from "graphql-tag";

function userFullName(user) {
  return user.firstName + ' ' + user.lastName;
}

export default {
  namespaced: false,
  state: {
    user: {},
    usingTrial: false,
    trialDays: 0,
  },
  getters: {
    getUser: (state) => (Id) => {
      // Get user object by Id, if specified, otherwise return current user.
      return !Id ? state.user : getObjectById(state.user.organization.users, Id);
    },
    getUserFullName: (state, getters) => (Id) => {
      // Get full first and last name of user by Id, if specified, otherwise return current user data.
      const user = getters.getUser(Id);
      return userFullName(user);
    },
    getUsers: (state) => () => {
      // Get list of objects with company user data {Id, name}.
      return state.user.organization.users.map(user => {
        return {
          Id: user.Id,
          name: userFullName(user),
        }
      })
    }
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    SET_TRIAL(state, user) {
      const organization = user.organization;
      const hasGearsMembership = user.membership === 'gears';

      // check if using trial
      if (!hasGearsMembership) {
        const dateNow = datetimeNow();
        const trialUntil = organization.gearsTrialUntil;
        state.usingTrial = trialUntil && dateNow <= trialUntil;

        // compute trial days
        if (state.usingTrial) {
          const trialMilliseconds = Math.abs(new Date(trialUntil) - new Date(dateNow));  // delta in milliseconds
          state.trialDays = trialMilliseconds / 8.64e7
        }
      } else {
        state.usingTrial = false;
        state.trialDays = 0;
      }
    }
  },
  actions: {
    async queryUser({ commit }, Id=undefined) {
      // Id arg optional, otherwise current user per JWT is queried.

      const query = {
        variables: { Id: Id },
        query: gql`query getUser($Id: ID) {
          user (Id: $Id) {
            ${requests.session.User}
          }
        }`,
      };
      const { data: { user } } = await apolloQuery(query);
      commit('SET_USER', user);
      commit('SET_TRIAL', user)
    },
  },
};
